import React, { createContext, useContext, useState, ReactNode, useRef, useEffect } from 'react'
import { WBSWorkItemType } from '../../types/Project/WBSWorkItemType'
import { WorkBreakdowns } from '../../types/Project/WorkBreakdowns'
import { ScaffoldRequestForm } from '../../types/Scaffold/ScaffoldRequest'
import { WorkItemTemplate } from '../../types/Scaffold/Scaffold'
import { createWorkItemTemplate, updateParentFromChildren } from '../../utils/ScaffoldWorkItemUtils'

interface WorkItemTemplatesContextType {
	// State
	workItemTemplates: WorkItemTemplate[]
	selectedWBS: WorkBreakdowns | null
	unitOfMeasure: string
	
	// Actions
	setWorkItemTemplates: (templates: WorkItemTemplate[]) => void
	setSelectedWBS: (selectedWBS: WorkBreakdowns | null) => void
	setUnitOfMeasure: (unitOfMeasure: string) => void
	addWorkItemTemplate: (wbsWorkItemType: WBSWorkItemType, parentId?: string) => void
	removeWorkItemTemplate: (id: string) => void
	updateWorkItemTemplate: (id: string, field: keyof WorkItemTemplate, value: any) => void
	resetState: () => void
	getIncrementId: () => () => number
	resetIdCounter: () => void
}

const WorkItemTemplatesContext = createContext<WorkItemTemplatesContextType | undefined>(undefined)

interface WorkItemTemplatesProviderProps {
	children: ReactNode
	scaffoldRequest: ScaffoldRequestForm | null
}

export function WorkItemTemplatesProvider({
	children,
	scaffoldRequest,
}: WorkItemTemplatesProviderProps) {
	const [workItemTemplates, setWorkItemTemplates] = useState<WorkItemTemplate[]>([])
	const [selectedWBS, setSelectedWBS] = useState<WorkBreakdowns | null>(null)
	const [unitOfMeasure, setUnitOfMeasure] = useState<string>('')
	const templateIdCounter = useRef(0)

	const incrementId = () => {
		return templateIdCounter.current++
	}

	/**
	 * Adds a new work item template to the hierarchy
	 */
	const addWorkItemTemplate = (wbsWorkItemType: WBSWorkItemType, parentId?: string) => {
		if (!selectedWBS) return

		if (parentId) {
			let found = false

			const addAsChild = (templates: WorkItemTemplate[]): WorkItemTemplate[] => {
				if (found) return templates

				return templates.map((template) => {
					if (found) return template

					if (template.id === parentId) {
						found = true
						return {
							...template,
							children: [
								...(template.children || []),
								createWorkItemTemplate(
									templateIdCounter.current,
									incrementId,
									selectedWBS,
									wbsWorkItemType,
									parentId,
									template.level + 1,
									scaffoldRequest || undefined,
									unitOfMeasure,
									scaffoldRequest?.po ?? undefined,
									scaffoldRequest?.workOrder ?? undefined,
								),
							],
						}
					}
					if (template.children && !found) {
						return {
							...template,
							children: addAsChild(template.children),
						}
					}
					return template
				})
			}

			setWorkItemTemplates(addAsChild(workItemTemplates))
		} else {
			// Add as root level sibling at the end
			setWorkItemTemplates([
				...workItemTemplates,
				createWorkItemTemplate(
					templateIdCounter.current,
					incrementId,
					selectedWBS,
					wbsWorkItemType,
					undefined,
					1,
					scaffoldRequest || undefined,
					unitOfMeasure,
					scaffoldRequest?.po ?? undefined,
					scaffoldRequest?.workOrder ?? undefined,
				),
			])
		}
	}

	/**
	 * Removes a work item template from the hierarchy
	 */
	const removeWorkItemTemplate = (id: string) => {
		let parentId: string | undefined = undefined
		const removeFromTemplates = (templates: WorkItemTemplate[]): WorkItemTemplate[] => {
			return templates
				.filter((template) => {
					if (template.id === id) {
						parentId = template.parentId
					}
					return template.id !== id
				})
				.map((template) => ({
					...template,
					children: template.children ? removeFromTemplates(template.children) : [],
				}))
		}

		let updatedTemplates = removeFromTemplates(workItemTemplates)
		while (parentId) {
			;[updatedTemplates, parentId] = updateParentFromChildren(parentId, updatedTemplates)
		}
		setWorkItemTemplates(updatedTemplates)
	}

	/**
	 * Updates a field in a work item template
	 */
	const updateWorkItemTemplate = (id: string, field: keyof WorkItemTemplate, value: any) => {
		let parentId: string | undefined = undefined
		const updateInTemplates = (templates: WorkItemTemplate[]): WorkItemTemplate[] => {
			return templates.map((template) => {
				if (template.id === id) {
					if (field === 'budgetedHours' || field === 'quantityBudgeted') {
						parentId = template.parentId
					}
					return { ...template, [field]: value }
				}
				if (template.children) {
					return {
						...template,
						children: updateInTemplates(template.children),
					}
				}
				return template
			})
		}
		let updatedTemplates = updateInTemplates(workItemTemplates)
		while (parentId) {
			;[updatedTemplates, parentId] = updateParentFromChildren(parentId, updatedTemplates)
		}
		setWorkItemTemplates(updatedTemplates)
	}

	/**
	 * Resets all state to initial values
	 */
	const resetState = () => {
		templateIdCounter.current = 0
		setWorkItemTemplates([])
		setSelectedWBS(null)
		setUnitOfMeasure('')
	}

	/**
	 * Returns the incrementId function for use in hierarchy initialization
	 */
	const getIncrementId = () => {
		return incrementId
	}

	/**
	 * Resets the ID counter (used when initializing new hierarchy)
	 */
	const resetIdCounter = () => {
		templateIdCounter.current = 0
	}

	const value: WorkItemTemplatesContextType = {
		workItemTemplates,
		selectedWBS,
		unitOfMeasure,
		setWorkItemTemplates,
		setSelectedWBS,
		setUnitOfMeasure,
		addWorkItemTemplate,
		removeWorkItemTemplate,
		updateWorkItemTemplate,
		resetState,
		getIncrementId,
		resetIdCounter,
	}

	return (
		<WorkItemTemplatesContext.Provider value={value}>{children}</WorkItemTemplatesContext.Provider>
	)
}

export function useWorkItemTemplates() {
	const context = useContext(WorkItemTemplatesContext)
	if (context === undefined) {
		throw new Error('useWorkItemTemplates must be used within a WorkItemTemplatesProvider')
	}
	return context
}
